/**
 * A message's body in its bubble (#1530): stored HTML rendered richly, held
 * images shown from their thumbnails, a tap on one opening the viewer.
 *
 * ⚠️ NEVER A WEBVIEW. A message renders through `RichHtmlView`, which is the
 * native `RenderHTML` core and nothing else; a table in a message renders
 * natively. The request-field "wide content" WebView branch lives in
 * `RichHtmlContent` and is not reachable from here.
 *
 * ⚠️ COLLAPSE IS BY HEIGHT, DECIDED FROM `onLayout`, AND CLIPPED FROM THE
 * FIRST PAINT. The old row estimated lines from the TEXT (48 characters a
 * line, newlines counted) and set `numberOfLines`; neither survives rich
 * content (a picture has no characters, a table has no newlines). So the
 * collapsed row is a box of `MESSAGE_COLLAPSED_HEIGHT` with its overflow
 * hidden, applied BEFORE the content has been measured, and the measurement
 * decides only whether a Show more control is offered. A row that fits is
 * clipped by nothing, because the clip is a ceiling it never reaches.
 *
 * `MESSAGE_COLLAPSED_HEIGHT` is DERIVED: six lines of body text, or one
 * image at its display ceiling plus a line PLUS the two paragraph margins
 * around them (a picture sits in its own `<p>`, and each block carries
 * `RICH_HTML_BLOCK_SPACING` below it), whichever is taller, so a collapsed
 * message always shows a whole picture. Without the margins the box was 4-8pt
 * short of a lone photo and offered Show more on a message with nothing more
 * to show.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { Platform, StyleSheet, View, useWindowDimensions } from 'react-native';

import { inlinePictureContentWidth } from './bubbleGeometry';
import { messageDisplay, pictureIndexForOrdinal } from './messageHtml';
import { RichHtmlView } from '../RichHtmlContent';
import {
  RICH_HTML_BLOCK_SPACING,
  RICH_HTML_BODY_LINE_HEIGHT,
  RICH_HTML_IMG_MAX_HEIGHT,
} from '../richText/richHtmlVisualContract';

import type { Message } from './correspondence.types';
import type { LayoutChangeEvent } from 'react-native';

/** How many lines of text a collapsed message shows. */
export const MESSAGE_TRUNCATE_LINES = 6;

/**
 * The collapsed box, derived (spec): max(6 lines, one image + one line +
 * the paragraph margins of both blocks).
 */
export const MESSAGE_COLLAPSED_HEIGHT = Math.max(
  MESSAGE_TRUNCATE_LINES * RICH_HTML_BODY_LINE_HEIGHT,
  RICH_HTML_IMG_MAX_HEIGHT +
    RICH_HTML_BODY_LINE_HEIGHT +
    2 * RICH_HTML_BLOCK_SPACING,
);

/** Whether a measured content height needs a Show more control. */
export const isBodyHeightTruncatable = (contentHeight: number): boolean =>
  contentHeight > MESSAGE_COLLAPSED_HEIGHT;

interface Props {
  message: Pick<Message, 'id' | 'body' | 'attachments' | 'mentions'>;
  /** Whether the row is expanded; the ROW owns the state and the control. */
  expanded: boolean;
  /**
   * The content's measured height, from `onLayout`. The row decides from it
   * whether to offer the control (`isBodyHeightTruncatable`).
   */
  onContentHeight: (height: number) => void;
  /**
   * A tap on a held image, as its index among the message's pictures, the
   * same index the file rows and the viewer use. Unmatched taps are dropped.
   * ⚠️ Give a STABLE callback: the body is memoised on its props, and a
   * fresh function each render re-renders every bubble in the list.
   */
  onOpenPicture?: (pictureIndex: number) => void;
  /**
   * #1599 Story 1.5: the viewer's id, so their OWN mention token is tinted
   * and labelled "…, you" (`resolveMentionTokens`). A string, so the memo
   * holds.
   */
  viewerId?: string;
  /**
   * #1599 Story 1.7: the viewer holds Configuration Engineer, so the team
   * token ("@Vertiv Config Team") is theirs too and is tinted. Decided once
   * by the panel; a boolean, so the memo holds.
   */
  viewerIsConfigTeam?: boolean;
}

/**
 * `React.memo` on the outside and `useMemo` on the inside: the list
 * re-renders on every keystroke in the composer and every scroll tick, and
 * sanitizing a body is several regex passes over its markup. The display is
 * recomputed only when the body or the files change.
 */
const MessageBodyComponent: React.FC<Props> = ({
  message,
  expanded,
  onContentHeight,
  onOpenPicture,
  viewerId,
  viewerIsConfigTeam = false,
}) => {
  const { body, attachments, mentions } = message;
  // #1599 Story 1.4: token text from `mentions` (a new array only when the
  // message changes, so the memo still holds). Story 1.5: and whose token is
  // "you". Story 1.7: and whether the team token is "you".
  const { html, imageIds } = useMemo(
    () =>
      messageDisplay(body, attachments, mentions, viewerId, viewerIsConfigTeam),
    [body, attachments, mentions, viewerId, viewerIsConfigTeam],
  );
  /**
   * #1710: the width a held picture is scaled into comes from the window and
   * the bubble's fixed geometry, never from measuring this bubble, because
   * this bubble is sized by the picture. On native `RichHtmlView` already
   * reads this hook per bubble, so nothing new re-renders there; on web the
   * DOM view does not, so each bubble now also subscribes to window resize,
   * a rare event, for a value the web view accepts and ignores.
   */
  const { width: windowWidth } = useWindowDimensions();
  const contentWidth = inlinePictureContentWidth(windowWidth);
  const onLayout = useCallback(
    (event: LayoutChangeEvent) => {
      onContentHeight(Math.round(event.nativeEvent.layout.height));
    },
    [onContentHeight],
  );
  // By ORDINAL among the body's images, never by src: two held files can
  // carry identical thumbnail bytes, and a src lookup opened the first for
  // both (C9).
  const onImagePress = useCallback(
    (_src: string, ordinal: number) => {
      if (!onOpenPicture) return;
      const index = pictureIndexForOrdinal(attachments, imageIds, ordinal);
      if (index >= 0) onOpenPicture(index);
    },
    [attachments, imageIds, onOpenPicture],
  );

  /**
   * ⚠️ THE COLLAPSED CLIP IS `overflow: 'scroll'` ON NATIVE, AND THAT IS THE
   * WHOLE FIX FOR "LONG MESSAGES ARE CUT OFF WITH NO SHOW MORE" (#1530 QA,
   * Request #13140; measured 2026-10-16). It still clips: iOS sets
   * `clipsToBounds` for any overflow but `visible` (`RCTViewManager.m:209`)
   * and Android clips in `dispatchDraw` the same way (`ReactViewGroup.kt:869`).
   *
   * Why it matters: with `overflow: 'hidden'` Yoga hands this box's 188 DOWN
   * to the content it is measuring as an "at most" height
   * (`CalculateLayout.cpp:165`, the browser-compatibility clause), and under
   * React Native's `YGErrataAll` (`RCTShadowView.m:53`,
   * `ReactYogaConfigProvider.kt:27`) two things then report 188 instead of
   * the truth: the non-scrolling `ScrollView` inside `RichHtmlView`, which
   * is `flexShrink: 1` by RN default and shrinks to fit, and on iOS any
   * single `Text`, which returns `MIN(natural, offered)`
   * (`RCTTextShadowView.mm:445`). So `onLayout` said 188 for a body of any
   * length, `isBodyHeightTruncatable(188)` is false, and the control never
   * appeared. A scroll container is the one kind Yoga does NOT pass its
   * height into, so the content measures at its true height while the box
   * stays capped from the first paint.
   *
   * Measured, not reasoned: the simulator logged exactly 188 for a nine-line
   * body, and Yoga 3.2.1 with `Errata.All` gives 188 for today's tree and
   * the true height for this one in every variant (removing the ScrollView
   * alone does NOT fix the single-paragraph case on iOS). Web keeps `hidden`:
   * the DOM never constrains a child by its parent's max-height, so web was
   * never affected, and CSS `scroll` would draw a scrollbar on every bubble.
   * `Platform.OS` is read here, in the body, never at module scope
   * (correspondence.md).
   */
  const collapsedStyle =
    Platform.OS === 'web' ? styles.collapsedWeb : styles.collapsedNative;

  return (
    <View
      style={expanded ? styles.open : collapsedStyle}
      testID={`correspondence-message-body-clip-${message.id}`}
    >
      <View
        onLayout={onLayout}
        testID={`correspondence-message-body-content-${message.id}`}
      >
        <RichHtmlView
          html={html}
          contentWidth={contentWidth}
          testID={`correspondence-message-body-rich-${message.id}`}
          onImagePress={onOpenPicture ? onImagePress : undefined}
        />
      </View>
    </View>
  );
};

export const MessageBody = React.memo(MessageBodyComponent);

/**
 * A hook for the row: the measured height and whether it needs the control.
 * Kept beside the body so the threshold has one home.
 */
export const useBodyMeasure = (): {
  truncatable: boolean;
  onContentHeight: (height: number) => void;
} => {
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const onContentHeight = useCallback((height: number) => {
    setContentHeight(prev => (prev === height ? prev : height));
  }, []);
  return {
    truncatable:
      contentHeight !== null && isBodyHeightTruncatable(contentHeight),
    onContentHeight,
  };
};

const styles = StyleSheet.create({
  // See `collapsedStyle` in the component for why these differ by platform.
  collapsedNative: {
    maxHeight: MESSAGE_COLLAPSED_HEIGHT,
    overflow: 'scroll',
  },
  collapsedWeb: {
    maxHeight: MESSAGE_COLLAPSED_HEIGHT,
    overflow: 'hidden',
  },
  open: {},
});
